import React, { useEffect, useState, useCallback } from "react";
import { Banknote, Loader2, Plus, Users, Check, X, Edit2, Play } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_STRUCTURE = {
  basic: 0, hra: 0, special: 0, conveyance: 0, medical: 0, other_allowances: 0,
  deductions: { pf: 0, esi: 0, tds: 0, other: 0 },
};

const STATUS_PILL = {
  pending_hr: "bg-amber-100 text-amber-700",
  hr_approved: "bg-indigo-100 text-indigo-700",
  processed: "bg-emerald-100 text-emerald-700",
};

const PayrollPanel = ({ authAxios }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null); // full run doc
  const [now] = useState(() => new Date());
  const [genYear, setGenYear] = useState(now.getFullYear());
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
  const [generating, setGenerating] = useState(false);
  const [editEmp, setEditEmp] = useState(null); // username for structure editor

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/payroll/runs`);
      setRuns(r.data?.runs || []);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const openDetail = async (id) => {
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/payroll/runs/${id}`);
      setOpenRun(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to load run");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/payroll/run/${genYear}/${genMonth}/generate`);
      await fetchRuns();
      if (r.data?.run_id) await openDetail(r.data.run_id);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to generate run");
    }
    setGenerating(false);
  };

  const hrApprove = async (runId, itemId) => {
    try {
      await authAxios.post(`${API_URL}/api/admin/payroll/runs/${runId}/items/${itemId}/hr-approve`);
      await openDetail(runId); await fetchRuns();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const financeProcess = async (runId, itemId) => {
    const ref = window.prompt("Payment reference (bank UTR / cheque no):", "");
    if (!ref) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/payroll/runs/${runId}/items/${itemId}/finance-process`, {
        payment_reference: ref,
        payment_method: "BANK_TRANSFER",
      });
      await openDetail(runId); await fetchRuns();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="payroll-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#E83E8C] flex items-center justify-center">
            <Banknote size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-bold text-[#1E1B4B] text-sm">Payroll</h3>
            <p className="text-xs text-[#94a3b8]">
              Salary structure · HR approval · Finance processing (manual mark-paid).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditEmp("__list__")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold hover:bg-gray-200"
            data-testid="open-salary-structures">
            <Users size={12} /> Salary Structures
          </button>
          <select value={genYear} onChange={(e) => setGenYear(Number(e.target.value))}
            className="text-[11px] px-1.5 py-1 border rounded">
            {[now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={genMonth} onChange={(e) => setGenMonth(Number(e.target.value))}
            className="text-[11px] px-1.5 py-1 border rounded">
            {Array.from({length: 12}, (_, i) => i+1).map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
          </select>
          <button onClick={generate} disabled={generating}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-[11px] font-bold hover:bg-[#c22d78] disabled:opacity-50"
            data-testid="generate-payroll-run">
            {generating ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Generate
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-[#E83E8C]" /></div>
      ) : runs.length === 0 ? (
        <p className="text-xs text-[#94a3b8] text-center py-4">No payroll runs yet. Set up a salary structure for employees, then click Generate.</p>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {runs.map((r) => (
            <div key={r.id} onClick={() => openDetail(r.id)}
              className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 hover:border-[#E83E8C]/30 cursor-pointer"
              data-testid={`payroll-run-${r.id}`}>
              <div className="font-mono text-[11px] text-gray-500">{r.year}-{String(r.month).padStart(2, "0")}</div>
              <div className="flex-1 flex gap-2 text-[11px]">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{r.item_count} items</span>
                <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">{r.hr_approved_count} approved</span>
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{r.processed_count} processed</span>
              </div>
              <div className="text-[11px] font-bold text-[#1E1B4B]">₹{(r.total_net_amount || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}

      {openRun && (
        <PayrollRunModal
          run={openRun}
          onClose={() => setOpenRun(null)}
          onHrApprove={(itemId) => hrApprove(openRun.id, itemId)}
          onFinanceProcess={(itemId) => financeProcess(openRun.id, itemId)}
        />
      )}

      {editEmp && <SalaryStructuresModal authAxios={authAxios} onClose={() => setEditEmp(null)} />}
    </div>
  );
};

const PayrollRunModal = ({ run, onClose, onHrApprove, onFinanceProcess }) => (
  <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="payroll-run-modal">
    <div className="bg-white rounded-2xl max-w-4xl w-full p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-[#1E1B4B]">Payroll run · {run.year}-{String(run.month).padStart(2, "0")}</h3>
          <p className="text-[11px] text-gray-400">{run.id} · {run.date_from} → {run.date_to} ({run.total_days} days)</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-gray-50 text-gray-500 uppercase">
            <tr>
              <th className="px-2 py-1.5 text-left">Employee</th>
              <th className="px-2 py-1.5 text-left">Role</th>
              <th className="px-2 py-1.5 text-right">Days</th>
              <th className="px-2 py-1.5 text-right">Gross</th>
              <th className="px-2 py-1.5 text-right">Ded</th>
              <th className="px-2 py-1.5 text-right">Net</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(run.items || []).map((i) => (
              <tr key={i.item_id} className="border-t border-gray-100" data-testid={`payroll-item-${i.item_id}`}>
                <td className="px-2 py-1.5 font-semibold">{i.name} <span className="text-gray-400 font-mono">@{i.username}</span></td>
                <td className="px-2 py-1.5 font-mono text-gray-500">{i.role}</td>
                <td className="px-2 py-1.5 text-right">{i.attendance?.effective_days ?? 0}/{i.total_days}</td>
                <td className="px-2 py-1.5 text-right">₹{i.gross?.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right text-red-500">-₹{i.total_deductions?.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right font-bold">₹{i.net?.toLocaleString()}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_PILL[i.status] || "bg-gray-100"}`}>
                    {i.status}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  {i.status === "pending_hr" && (
                    <button onClick={() => onHrApprove(i.item_id)}
                      className="px-2 py-0.5 rounded bg-indigo-500 text-white text-[10px] font-bold flex items-center gap-0.5"
                      data-testid={`hr-approve-${i.item_id}`}>
                      <Check size={10} /> HR Approve
                    </button>
                  )}
                  {i.status === "hr_approved" && (
                    <button onClick={() => onFinanceProcess(i.item_id)}
                      className="px-2 py-0.5 rounded bg-emerald-500 text-white text-[10px] font-bold flex items-center gap-0.5"
                      data-testid={`finance-process-${i.item_id}`}>
                      <Check size={10} /> Mark Paid
                    </button>
                  )}
                  {i.status === "processed" && <span className="text-[10px] text-gray-400">ref {i.payment_reference}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const SalaryStructuresModal = ({ authAxios, onClose }) => {
  const [managers, setManagers] = useState([]);
  const [picked, setPicked] = useState("");
  const [structure, setStructure] = useState(EMPTY_STRUCTURE);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authAxios.get(`${API_URL}/api/admin/managers`);
        if (!alive) return;
        setManagers((r.data || []).filter((m) => m.active && m.verification_status === "approved"));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [authAxios]);

  useEffect(() => {
    if (!picked) return;
    setSavedOk(false);
    (async () => {
      try {
        const r = await authAxios.get(`${API_URL}/api/admin/payroll/structure/${encodeURIComponent(picked)}`);
        if (r.data?.exists) {
          setStructure({
            basic: r.data.basic, hra: r.data.hra, special: r.data.special,
            conveyance: r.data.conveyance, medical: r.data.medical,
            other_allowances: r.data.other_allowances,
            deductions: r.data.deductions || EMPTY_STRUCTURE.deductions,
          });
        } else {
          setStructure(EMPTY_STRUCTURE);
        }
      } catch { setStructure(EMPTY_STRUCTURE); }
    })();
  }, [authAxios, picked]);

  const save = async () => {
    if (!picked) return;
    setSaving(true); setSavedOk(false);
    try {
      await authAxios.put(`${API_URL}/api/admin/payroll/structure/${encodeURIComponent(picked)}`, structure);
      setSavedOk(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save structure");
    }
    setSaving(false);
  };

  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

  return (
    <div className="fixed inset-0 z-[85] bg-black/60 flex items-center justify-center p-4" data-testid="salary-structures-modal">
      <div className="bg-white rounded-2xl max-w-xl w-full p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#1E1B4B]">Salary Structures</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <label className="text-[11px] font-semibold text-[#1E1B4B] block mb-1">Employee</label>
        <select value={picked} onChange={(e) => setPicked(e.target.value)}
          className="w-full px-3 py-2 text-sm border rounded-xl mb-3"
          data-testid="salary-employee-select">
          <option value="">— pick an employee —</option>
          {managers.map((m) => <option key={m.username} value={m.username}>{m.name} · {m.role}</option>)}
        </select>

        {picked && (
          <>
            <p className="text-[11px] font-bold text-[#1E1B4B] mt-2 mb-1">Earnings (monthly ₹)</p>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {[
                ["basic", "Basic"], ["hra", "HRA"], ["special", "Special"],
                ["conveyance", "Conveyance"], ["medical", "Medical"], ["other_allowances", "Other allowances"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-gray-500">{lbl}</label>
                  <input type="number" min="0"
                    value={structure[k]}
                    onChange={(e) => setStructure({ ...structure, [k]: num(e.target.value) })}
                    className="w-full px-2 py-1 border rounded"
                    data-testid={`salary-${k}`} />
                </div>
              ))}
            </div>
            <p className="text-[11px] font-bold text-[#1E1B4B] mt-3 mb-1">Deductions (monthly ₹)</p>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {["pf", "esi", "tds", "other"].map((k) => (
                <div key={k}>
                  <label className="text-gray-500 uppercase">{k}</label>
                  <input type="number" min="0"
                    value={structure.deductions[k]}
                    onChange={(e) => setStructure({
                      ...structure,
                      deductions: { ...structure.deductions, [k]: num(e.target.value) },
                    })}
                    className="w-full px-2 py-1 border rounded"
                    data-testid={`deduction-${k}`} />
                </div>
              ))}
            </div>
            {savedOk && <p className="text-[11px] text-emerald-600 mt-2">Saved ✓</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-xs rounded-xl border">Close</button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 text-xs rounded-xl bg-[#E83E8C] text-white font-bold flex items-center gap-1 disabled:opacity-50"
                data-testid="save-salary-structure">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PayrollPanel;
